import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box,
} from '@mui/material';
import InvButton from '../../../components/InvButton';
import { InvSelect, InvTextField } from '../../../components/InvField';
import { shortCloseLineCount } from '../../../utils/orderStatusUpdate';
import { IPD_COLORS, cardSx } from '../../../theme';

/**
 * Close every line the store cannot supply, in one pass.
 *
 * On a 12-medicine order with four unavailable lines the per-line path cost 16
 * clicks (button, dropdown, reason, confirm — four times over) to record a fact
 * the stock check had already established. This shows the same decision as one
 * reviewable list.
 *
 * Reason is per GROUP, not per dialog: POST /update_ipd_prescription_status/
 * takes one reason for the whole prescription_ids array, so out-of-stock and
 * not-in-formulary lines cannot share a call without one of them being recorded
 * under a cause that is not true (see groupLinesForShortClose).
 *
 * Every reason stays editable. The stock status says why the shelf is empty, not
 * why the pharmacy is declining the line — "Batch Expired" and "Patient Left"
 * both present as out-of-stock, and this is the clinical record of why an order
 * died, so the pharmacist gets the last word on it.
 */
export default function BulkShortCloseDialog({
  open,
  groups = [],
  reasons = [],
  loading = false,
  onConfirm,
  onClose,
}) {
  // reason + free text per group, keyed by the group's auto-reason.
  const [edits, setEdits] = useState({});

  // Seeded per opening so a dialog reopened after a store change does not carry
  // the previous store's reasons.
  useEffect(() => {
    if (!open) return;
    setEdits(Object.fromEntries(
      groups.map((group) => [group.defaultReason, { reason: group.defaultReason, note: '' }]),
    ));
    // groups is rebuilt on every render of the parent; keying the reset on `open`
    // alone is deliberate, otherwise the pharmacist's edits are wiped mid-dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resolved = groups.map((group) => {
    const edit = edits[group.defaultReason] || { reason: group.defaultReason, note: '' };
    const reason = edit.reason === 'Other' ? edit.note.trim() : edit.reason;
    return { ...group, reason, picked: edit.reason, note: edit.note };
  });

  const total = shortCloseLineCount(groups);
  const canConfirm = !loading && resolved.length > 0 && resolved.every((g) => !!g.reason);

  function patch(key, next) {
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], ...next } }));
  }

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose?.()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { ...cardSx, borderRadius: '14px' } }}
    >
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary, pb: 1 }}>
        Mark {total} medicine{total === 1 ? '' : 's'} as not supplied
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 2 }}>
          The ward stops waiting for these and their remaining dose schedule is
          retired. This cannot be undone — the rest of the order stays fillable.
        </Typography>

        {resolved.map((group) => (
          <Box
            key={group.defaultReason}
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: '10px',
              border: `1px solid ${IPD_COLORS.border}`,
              backgroundColor: IPD_COLORS.panelHeaderBg,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: IPD_COLORS.textSecondary, mb: 1 }}>
              {group.lines.length} medicine{group.lines.length === 1 ? '' : 's'}
            </Typography>
            {group.lines.map((line) => (
              <Typography key={line.id} sx={{ fontSize: 13, color: IPD_COLORS.textPrimary, mb: 0.25 }}>
                · {line.medicine || `#${line.id}`}
              </Typography>
            ))}
            <Box sx={{ mt: 1.5 }}>
              <InvSelect
                label="Reason *"
                value={group.picked}
                onChange={(e) => patch(group.defaultReason, { reason: e.target.value })}
                options={reasons.map((value) => ({ value, label: value }))}
                fullWidth
              />
            </Box>
            {group.picked === 'Other' && (
              <InvTextField
                label="Details *"
                value={group.note}
                onChange={(e) => patch(group.defaultReason, { note: e.target.value })}
                placeholder="What actually happened"
                fullWidth
                sx={{ mt: 1.5 }}
              />
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <InvButton variant="outlined" tone="teal" disabled={loading} onClick={onClose}>
          Back
        </InvButton>
        <InvButton
          loading={loading}
          disabled={!canConfirm}
          onClick={() => onConfirm?.(resolved.map((g) => ({
            reason: g.reason,
            ids: g.lines.map((l) => l.id),
          })))}
        >
          Mark {total} line{total === 1 ? '' : 's'}
        </InvButton>
      </DialogActions>
    </Dialog>
  );
}
